/**
 * Transaction Service
 * 
 * Service for managing transactions using Supabase client
 * Following the service pattern from accounts feature
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import {
  Transaction,
  TransactionQueryParams,
  TransactionListResponse,
  CreateTransactionInput,
  UpdateTransactionInput,
  ServiceResponse,
  DeleteResponse,
  SpendingByCategoryParams,
  SpendingByCategoryResponse,
  SpendingByCategoryItem,
  SpendingByCategoryOverTimeResponse
} from '../types/transaction.types';
import { differenceInDays, startOfDay, startOfWeek, startOfMonth, format, parseISO } from 'date-fns';
import { validateCreateTransaction, validateUpdateTransaction } from '../utils/transaction-validation';
import { CategoryType } from '../types/category.types';

// Helper function to convert database row to Transaction
function convertDbRowToTransaction(row: any): Transaction {
  return {
    id: row.id,
    userId: row.user_id,
    accountId: row.account_id,
    categoryId: row.category_id || undefined,
    amount: parseFloat(row.amount) || 0,
    description: row.description || '',
    merchantName: row.merchant_name || undefined,
    date: row.date,
    type: row.type,
    status: row.status || 'pending',
    notes: row.notes || undefined,
    referenceNumber: row.reference_number || undefined,
    payee: row.payee || undefined,
    balanceAfter: row.balance_after ? parseFloat(row.balance_after) : undefined,
    sourceAccountId: row.source_account_id || undefined,
    destinationAccountId: row.destination_account_id || undefined,
    externalId: row.external_id || undefined,
    originalDescription: row.original_description || undefined,
    chequeNumber: row.cheque_number || undefined,
    autoCategorized: row.auto_categorized || false,
    confidenceScore: row.confidence_score ? parseFloat(row.confidence_score) : undefined,
    flaggedForReview: row.flagged_for_review || false,
    statusReason: row.status_reason || undefined,
    reviewedBy: row.reviewed_by || undefined,
    reviewedAt: row.reviewed_at || undefined,
    archivedAt: row.archived_at || undefined,
    businessUsePercent:
      row.business_use_percent != null ? Number(row.business_use_percent) : undefined,
    gstStatus: row.gst_status ?? undefined,
    receiptAttached: row.receipt_attached ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

// Helper function to convert Transaction to database insert format
function convertTransactionToDbInsert(input: CreateTransactionInput, userId: string): any {
  // If a category is provided, set status to 'completed' (categorised), otherwise 'pending' (uncategorised)
  const status = input.categoryId ? 'completed' : 'pending';
  
  return {
    user_id: userId,
    account_id: input.accountId,
    category_id: input.categoryId || null,
    amount: input.amount,
    description: input.description,
    merchant_name: input.merchantName || null,
    date: input.date,
    type: input.type,
    status: status,
    notes: input.notes || null,
    reference_number: input.referenceNumber || null,
    payee: input.payee || null,
    balance_after: input.balanceAfter || null,
    source_account_id: input.sourceAccountId || null,
    destination_account_id: input.destinationAccountId || null,
    external_id: input.externalId || null,
    original_description: input.originalDescription || null,
    cheque_number: input.chequeNumber || null
  };
}

class TransactionService {
  /**
   * List transactions with filtering and pagination
   */
  async listTransactions(params: TransactionQueryParams): Promise<ServiceResponse<TransactionListResponse>> {
    try {
      const {
        userId,
        accountId,
        accountIds,
        categoryId,
        categoryIds,
        status,
        type,
        dateFrom,
        dateTo,
        search,
        page = 1,
        limit = 50,
        sortOrder = 'desc'
      } = params;

      let query = supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .is('archived_at', null) // Exclude archived transactions by default
        .order('date', { ascending: sortOrder === 'asc' });

      // Apply filters
      // accountIds takes precedence over accountId if both are provided
      if (accountIds && accountIds.length > 0) {
        query = query.in('account_id', accountIds);
      } else if (accountId) {
        query = query.eq('account_id', accountId);
      }

      if (categoryIds && categoryIds.length > 0) {
        query = query.in('category_id', categoryIds);
      } else if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      // "pending" / Uncategorised is defined by missing category so the badge and
      // status filter stay correct even if status drifts out of sync.
      if (status === 'pending') {
        query = query.is('category_id', null);
      } else if (status) {
        query = query.eq('status', status);
      }

      if (type) {
        query = query.eq('type', type);
      }

      if (dateFrom) {
        query = query.gte('date', dateFrom);
      }

      if (dateTo) {
        query = query.lte('date', dateTo);
      }

      if (search) {
        query = query.or(`description.ilike.%${search}%,merchant_name.ilike.%${search}%,notes.ilike.%${search}%`);
      }

      // Apply pagination
      // Note: Supabase has a default limit of 1000 rows per query
      // When limit is large (e.g., MAX_ALL_LIMIT = 5000), we need to explicitly set a limit
      // to override the default 1000 limit
      if (limit >= 1000) {
        // For large limits, use limit() instead of range() to avoid Supabase's 1000 row default
        query = query.limit(limit);
      } else {
        // For normal pagination, use range()
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch transactions: ${error.message}`
          }
        };
      }

      return {
        data: {
          transactions: (data || []).map(convertDbRowToTransaction),
          total: count || 0,
          page,
          limit
        }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Get archived transactions with filtering and pagination
   * Archived transactions are those with archived_at IS NOT NULL
   */
  async getArchivedTransactions(params: TransactionQueryParams): Promise<ServiceResponse<TransactionListResponse>> {
    try {
      const {
        userId,
        accountId,
        accountIds,
        categoryId,
        categoryIds,
        status,
        type,
        dateFrom,
        dateTo,
        search,
        page = 1,
        limit = 50,
        sortOrder = 'desc'
      } = params;

      let query = supabase
        .from('transactions')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .not('archived_at', 'is', null) // Only include archived transactions
        .order('date', { ascending: sortOrder === 'asc' });

      // Apply filters
      // accountIds takes precedence over accountId if both are provided
      if (accountIds && accountIds.length > 0) {
        query = query.in('account_id', accountIds);
      } else if (accountId) {
        query = query.eq('account_id', accountId);
      }

      if (categoryIds && categoryIds.length > 0) {
        query = query.in('category_id', categoryIds);
      } else if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (status === 'pending') {
        query = query.is('category_id', null);
      } else if (status) {
        query = query.eq('status', status);
      }

      if (type) {
        query = query.eq('type', type);
      }

      if (dateFrom) {
        query = query.gte('date', dateFrom);
      }

      if (dateTo) {
        query = query.lte('date', dateTo);
      }

      if (search) {
        query = query.or(`description.ilike.%${search}%,merchant_name.ilike.%${search}%,notes.ilike.%${search}%`);
      }

      // Apply pagination
      // Note: Supabase has a default limit of 1000 rows per query
      // When limit is large (e.g., MAX_ALL_LIMIT = 5000), we need to explicitly set a limit
      // to override the default 1000 limit
      if (limit >= 1000) {
        // For large limits, use limit() instead of range() to avoid Supabase's 1000 row default
        query = query.limit(limit);
      } else {
        // For normal pagination, use range()
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch archived transactions: ${error.message}`
          }
        };
      }

      return {
        data: {
          transactions: (data || []).map(convertDbRowToTransaction),
          total: count || 0,
          page,
          limit
        }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred while fetching archived transactions'
        }
      };
    }
  }

  /**
   * Get aggregated spending grouped by category (expense transactions only)
   */
  async getSpendingByCategory(params: SpendingByCategoryParams): Promise<ServiceResponse<SpendingByCategoryResponse>> {
    try {
      const { userId, dateFrom, dateTo } = params;
      const includeExcluded = params.includeExcluded ?? false;

      if (!userId) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'userId is required'
          }
        };
      }

      const groupingMode = params.grouping ?? 'parent';

      let query = supabase
        .from('transactions')
        .select(
          `
            amount,
            type,
            category_id,
            categories (
              id,
              name,
              slug,
              color,
              icon,
              type,
              parent_id,
              exclude_from_spending
            )
          `
        )
        .eq('user_id', userId)
        .eq('type', 'debit')
        .in('status', ['completed', 'pending']); // Include both completed and pending transactions

      if (dateFrom) {
        query = query.gte('date', dateFrom);
      }

      if (dateTo) {
        query = query.lte('date', dateTo);
      }

      const { data, error } = await query;

      if (error) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch spending by category: ${error.message}`
          }
        };
      }

      type CategoryInfo = {
        id: string;
        parentId: string | null;
        name: string;
        slug?: string;
        color?: string;
        icon?: string;
        type: 'income' | 'expense' | 'transfer';
        excludeFromSpending: boolean;
      };

      const categoryDetails = new Map<string, CategoryInfo>();
      const parentIdsToFetch = new Set<string>();

      for (const row of data || []) {
        const category = row.categories;
        if (category?.id && !categoryDetails.has(category.id)) {
          categoryDetails.set(category.id, {
            id: category.id,
            parentId: category.parent_id ?? null,
            name: category.name ?? 'Uncategorised',
            slug: category.slug ?? undefined,
            color: category.color ?? undefined,
            icon: category.icon ?? undefined,
            type: (category.type as 'income' | 'expense' | 'transfer') ?? 'expense',
            excludeFromSpending: category.exclude_from_spending ?? false,
          });

          if (category.parent_id) {
            parentIdsToFetch.add(category.parent_id);
          }
        }
      }

      const missingParentIds = Array.from(parentIdsToFetch).filter((parentId) => !categoryDetails.has(parentId));

      if (missingParentIds.length > 0) {
        let parentQuery = supabase
          .from('categories')
          .select('id, parent_id, name, slug, color, icon, type, exclude_from_spending')
          .or(`user_id.eq.${userId},is_system.eq.true`);

        parentQuery = parentQuery.in('id', missingParentIds);

        const { data: parentRows, error: parentError } = await parentQuery;

        if (parentError) {
          console.error('Failed to fetch parent categories for spending report:', parentError);
        } else {
          for (const parent of parentRows || []) {
            if (parent?.id) {
              categoryDetails.set(parent.id, {
                id: parent.id,
                parentId: parent.parent_id ?? null,
                name: parent.name ?? 'Uncategorised',
                slug: parent.slug ?? undefined,
                color: parent.color ?? undefined,
                icon: parent.icon ?? undefined,
                type: (parent.type as 'income' | 'expense' | 'transfer') ?? 'expense',
                excludeFromSpending: parent.exclude_from_spending ?? false,
              });
            }
          }
        }
      }

      const totals = new Map<
        string,
        {
          categoryId: string | null;
          name: string;
          slug?: string;
          color?: string;
          icon?: string;
          type: 'income' | 'expense' | 'transfer';
          amount: number;
          relatedCategoryIds: Set<string>;
          isExcluded: boolean;
        }
      >();

      let processedTransactionCount = 0;

      for (const row of data || []) {
        const rawAmount = Math.abs(typeof row.amount === 'string' ? Number.parseFloat(row.amount) : Number(row.amount) || 0);
        if (rawAmount <= 0) {
          continue;
        }

        const category = row.categories;
        const categoryId = category?.id ?? null;
        const categoryInfo = categoryId ? categoryDetails.get(categoryId) : undefined;

        let targetInfo: CategoryInfo | undefined = categoryInfo;

        if (groupingMode === 'parent' && categoryInfo?.parentId) {
          const parentInfo = categoryDetails.get(categoryInfo.parentId);
          if (parentInfo) {
            targetInfo = parentInfo;
          }
        }

        const entryKey = targetInfo?.id ?? 'uncategorised';
        const isExcludedRow =
          (categoryInfo?.excludeFromSpending ?? false) ||
          (targetInfo?.excludeFromSpending ?? false);

        if (!includeExcluded && isExcludedRow) {
          continue;
        }

        // Count this transaction as processed
        processedTransactionCount++;

        if (!totals.has(entryKey)) {
          totals.set(entryKey, {
            categoryId: targetInfo?.id ?? null,
            name: targetInfo?.name ?? 'Uncategorised',
            slug: targetInfo?.slug ?? undefined,
            color: targetInfo?.color ?? undefined,
            icon: targetInfo?.icon ?? undefined,
            type: (targetInfo?.type as CategoryType) ?? CategoryType.EXPENSE,
            amount: 0,
            relatedCategoryIds: new Set<string>(),
            isExcluded: isExcludedRow,
          });
        }

        const entry = totals.get(entryKey)!;
        entry.amount += rawAmount;
        entry.isExcluded = entry.isExcluded || isExcludedRow;

        if (groupingMode === 'parent') {
          if (categoryId) {
            entry.relatedCategoryIds.add(categoryId);
          }
          if (targetInfo?.id) {
            entry.relatedCategoryIds.add(targetInfo.id);
          }
        } else if (categoryId) {
          entry.relatedCategoryIds.add(categoryId);
        }
      }

      const items: SpendingByCategoryItem[] = Array.from(totals.values())
        .filter((item) => item.amount > 0)
        .map((item) => ({
          categoryId: item.categoryId,
          categoryName: item.name,
          categorySlug: item.slug,
          categoryColor: item.color,
          categoryIcon: item.icon,
          relatedCategoryIds: Array.from(item.relatedCategoryIds),
          totalAmount: item.amount,
          type: item.type as CategoryType,
          isExcluded: item.isExcluded,
        }));

      const total = items.reduce((sum, item) => sum + item.totalAmount, 0);

      return {
        data: {
          items,
          total,
          transactionCount: processedTransactionCount,
        }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error?.message || 'Unknown error occurred while aggregating spending by category'
        }
      };
    }
  }

  /**
   * Get spending by category over time (time-series data)
   */
  async getSpendingByCategoryOverTime(params: SpendingByCategoryParams): Promise<ServiceResponse<SpendingByCategoryOverTimeResponse>> {
    try {
      const { userId, dateFrom, dateTo } = params;
      const includeExcluded = params.includeExcluded ?? false;

      if (!userId) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'userId is required'
          }
        };
      }

      if (!dateFrom || !dateTo) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'dateFrom and dateTo are required'
          }
        };
      }

      const groupingMode = params.grouping ?? 'parent';

      // Determine granularity based on date range
      const startDate = parseISO(dateFrom);
      const endDate = parseISO(dateTo);
      const daysDiff = differenceInDays(endDate, startDate);
      
      let granularity: 'daily' | 'weekly' | 'monthly';
      if (daysDiff <= 30) {
        granularity = 'daily';
      } else if (daysDiff <= 90) {
        granularity = 'weekly';
      } else {
        granularity = 'monthly';
      }

      // Fetch all transactions in the date range.
      // Supabase caps responses at 1000 rows, so paginate to include full-year data.
      const PAGE_SIZE = 1000;
      type SpendingTrendRow = {
        amount: number | string;
        date: string;
        type: string;
        category_id: string | null;
        categories: {
          id: string;
          name: string | null;
          slug: string | null;
          color: string | null;
          icon: string | null;
          type: string | null;
          parent_id: string | null;
          exclude_from_spending: boolean | null;
        } | null;
      };

      const data: SpendingTrendRow[] = [];
      let offset = 0;

      while (true) {
        const { data: page, error } = await supabase
          .from('transactions')
          .select(
            `
              amount,
              date,
              type,
              category_id,
              categories (
                id,
                name,
                slug,
                color,
                icon,
                type,
                parent_id,
                exclude_from_spending
              )
            `
          )
          .eq('user_id', userId)
          .eq('type', 'debit')
          .in('status', ['completed', 'pending'])
          .is('archived_at', null)
          .gte('date', dateFrom)
          .lte('date', dateTo)
          .order('date', { ascending: true })
          .range(offset, offset + PAGE_SIZE - 1);

        if (error) {
          return {
            error: {
              code: 'QUERY_ERROR',
              message: `Failed to fetch spending by category over time: ${error.message}`,
            },
          };
        }

        if (!page?.length) {
          break;
        }

        data.push(...(page as SpendingTrendRow[]));

        if (page.length < PAGE_SIZE) {
          break;
        }

        offset += PAGE_SIZE;
      }

      // Build category details map (similar to getSpendingByCategory)
      type CategoryInfo = {
        id: string;
        parentId: string | null;
        name: string;
        slug?: string;
        color?: string;
        icon?: string;
        type: 'income' | 'expense' | 'transfer';
        excludeFromSpending: boolean;
      };

      const categoryDetails = new Map<string, CategoryInfo>();
      const parentIdsToFetch = new Set<string>();

      for (const row of data || []) {
        const category = row.categories;
        if (category?.id && !categoryDetails.has(category.id)) {
          categoryDetails.set(category.id, {
            id: category.id,
            parentId: category.parent_id ?? null,
            name: category.name ?? 'Uncategorised',
            slug: category.slug ?? undefined,
            color: category.color ?? undefined,
            icon: category.icon ?? undefined,
            type: (category.type as 'income' | 'expense' | 'transfer') ?? 'expense',
            excludeFromSpending: category.exclude_from_spending ?? false,
          });

          if (category.parent_id) {
            parentIdsToFetch.add(category.parent_id);
          }
        }
      }

      // Fetch parent categories if needed
      const missingParentIds = Array.from(parentIdsToFetch).filter((parentId) => !categoryDetails.has(parentId));

      if (missingParentIds.length > 0) {
        let parentQuery = supabase
          .from('categories')
          .select('id, parent_id, name, slug, color, icon, type, exclude_from_spending')
          .or(`user_id.eq.${userId},is_system.eq.true`);

        parentQuery = parentQuery.in('id', missingParentIds);

        const { data: parentRows, error: parentError } = await parentQuery;

        if (parentError) {
          console.error('Failed to fetch parent categories for spending trends:', parentError);
        } else {
          for (const parent of parentRows || []) {
            if (parent?.id) {
              categoryDetails.set(parent.id, {
                id: parent.id,
                parentId: parent.parent_id ?? null,
                name: parent.name ?? 'Uncategorised',
                slug: parent.slug ?? undefined,
                color: parent.color ?? undefined,
                icon: parent.icon ?? undefined,
                type: (parent.type as 'income' | 'expense' | 'transfer') ?? 'expense',
                excludeFromSpending: parent.exclude_from_spending ?? false,
              });
            }
          }
        }
      }

      // Group transactions by period and category
      const periodCategoryMap = new Map<string, Map<string, number>>();
      const categoryTotals = new Map<string, {
        categoryId: string | null;
        name: string;
        color?: string;
        icon?: string;
        amount: number;
      }>();
      let processedTransactionCount = 0;

      for (const row of data || []) {
        const rawAmount = Math.abs(typeof row.amount === 'string' ? Number.parseFloat(row.amount) : Number(row.amount) || 0);
        if (rawAmount <= 0) {
          continue;
        }

        const category = row.categories;
        const categoryId = category?.id ?? null;
        const categoryInfo = categoryId ? categoryDetails.get(categoryId) : undefined;

        let targetInfo: CategoryInfo | undefined = categoryInfo;

        if (groupingMode === 'parent' && categoryInfo?.parentId) {
          const parentInfo = categoryDetails.get(categoryInfo.parentId);
          if (parentInfo) {
            targetInfo = parentInfo;
          }
        }

        const entryKey = targetInfo?.id ?? 'uncategorised';
        const isExcludedRow =
          (categoryInfo?.excludeFromSpending ?? false) ||
          (targetInfo?.excludeFromSpending ?? false);

        if (!includeExcluded && isExcludedRow) {
          continue;
        }

        // Determine period key based on granularity
        const transactionDate = parseISO(row.date);
        let periodKey: string;
        
        if (granularity === 'daily') {
          periodKey = format(startOfDay(transactionDate), 'yyyy-MM-dd');
        } else if (granularity === 'weekly') {
          periodKey = format(startOfWeek(transactionDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        } else {
          periodKey = format(startOfMonth(transactionDate), 'yyyy-MM');
        }

        // Initialize period map if needed
        if (!periodCategoryMap.has(periodKey)) {
          periodCategoryMap.set(periodKey, new Map());
        }

        const categoryMap = periodCategoryMap.get(periodKey)!;

        // Add to period-category map
        const currentAmount = categoryMap.get(entryKey) || 0;
        categoryMap.set(entryKey, currentAmount + rawAmount);
        
        // Count this transaction as processed
        processedTransactionCount++;

        // Track category totals for top categories
        if (!categoryTotals.has(entryKey)) {
          categoryTotals.set(entryKey, {
            categoryId: targetInfo?.id ?? null,
            name: targetInfo?.name ?? 'Uncategorised',
            color: targetInfo?.color ?? undefined,
            icon: targetInfo?.icon ?? undefined,
            amount: 0,
          });
        }

        const categoryTotal = categoryTotals.get(entryKey)!;
        categoryTotal.amount += rawAmount;
      }

      // Get all categories sorted by total amount (component will handle filtering to top 5/10/all)
      const topCategories = Array.from(categoryTotals.values())
        .sort((a, b) => b.amount - a.amount);

      // Build response items - ensure all periods have all top categories
      const items: SpendingByCategoryOverTimeResponse['items'] = [];
      const sortedPeriods = Array.from(periodCategoryMap.keys()).sort();

      // If granularity is daily but date range spans multiple months, aggregate into monthly periods
      let periodsToProcess: string[];
      let aggregatedPeriodMap: Map<string, Map<string, number>> | null = null;

      if (granularity === 'daily' && daysDiff > 30) {
        // Aggregate daily periods into monthly periods
        aggregatedPeriodMap = new Map<string, Map<string, number>>();
        
        for (const dailyPeriod of sortedPeriods) {
          // Extract month from daily period (format: YYYY-MM-DD)
          const monthlyPeriod = dailyPeriod.substring(0, 7); // Get YYYY-MM
          
          if (!aggregatedPeriodMap.has(monthlyPeriod)) {
            aggregatedPeriodMap.set(monthlyPeriod, new Map());
          }
          
          const monthlyCategoryMap = aggregatedPeriodMap.get(monthlyPeriod)!;
          const dailyCategoryMap = periodCategoryMap.get(dailyPeriod)!;
          
          // Sum up all categories for this month
          for (const [categoryKey, amount] of dailyCategoryMap.entries()) {
            const currentAmount = monthlyCategoryMap.get(categoryKey) || 0;
            monthlyCategoryMap.set(categoryKey, currentAmount + amount);
          }
        }
        
        periodsToProcess = Array.from(aggregatedPeriodMap.keys()).sort();
        granularity = 'monthly'; // Update granularity for response
      } else {
        periodsToProcess = sortedPeriods;
      }

      for (const period of periodsToProcess) {
        const categoryMap = (aggregatedPeriodMap || periodCategoryMap).get(period)!;
        
        // Include all top categories for each period (even if amount is 0)
        for (const topCategory of topCategories) {
          const categoryKey = topCategory.categoryId ?? 'uncategorised';
          const amount = categoryMap.get(categoryKey) || 0;

          items.push({
            period,
            categoryId: topCategory.categoryId,
            categoryName: topCategory.name,
            categoryColor: topCategory.color,
            categoryIcon: topCategory.icon,
            amount,
            type: CategoryType.EXPENSE,
          });
        }
      }

      // Transaction count is already tracked in processedTransactionCount

      return {
        data: {
          items,
          granularity,
          topCategories: topCategories.map(cat => ({
            categoryId: cat.categoryId,
            categoryName: cat.name,
            categoryColor: cat.color,
            categoryIcon: cat.icon,
            totalAmount: cat.amount,
          })),
          transactionCount: processedTransactionCount,
        }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error?.message || 'Unknown error occurred while aggregating spending by category over time'
        }
      };
    }
  }

  /**
   * Get transaction by ID
   */
  async getTransactionById(transactionId: string): Promise<ServiceResponse<Transaction>> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated'
          }
        };
      }

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Transaction not found'
            }
          };
        }
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch transaction: ${error.message}`
          }
        };
      }

      return {
        data: convertDbRowToTransaction(data)
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Create new transaction
   */
  async createTransaction(input: CreateTransactionInput): Promise<ServiceResponse<Transaction>> {
    try {
      // Validate input
      const validation = validateCreateTransaction(input);
      if (!validation.success) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: validation.error?.issues.map((e: any) => e.message).join(', ') || 'Validation failed'
          }
        };
      }

      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated'
          }
        };
      }

      // Verify account belongs to user and get opening date
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .select('id, opening_date, created_at')
        .eq('id', input.accountId)
        .eq('user_id', userId)
        .single();

      if (accountError || !account) {
        return {
          error: {
            code: 'FORBIDDEN',
            message: 'Account not found or does not belong to user'
          }
        };
      }

      // For transfers, also verify destination account
      let destinationAccount: any = null;
      if (input.type === 'transfer' && input.destinationAccountId) {
        const { data: destAccount, error: destAccountError } = await supabase
          .from('accounts')
          .select('id, opening_date, created_at')
          .eq('id', input.destinationAccountId)
          .eq('user_id', userId)
          .single();

        if (destAccountError || !destAccount) {
          return {
            error: {
              code: 'FORBIDDEN',
              message: 'Destination account not found or does not belong to user'
            }
          };
        }
        destinationAccount = destAccount;
      }

      // Verify category belongs to user if provided
      if (input.categoryId) {
        const { data: category, error: categoryError } = await supabase
          .from('categories')
          .select('id, user_id, is_system')
          .eq('id', input.categoryId)
          .single();

        if (categoryError || !category) {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found'
            }
          };
        }

        // Verify it belongs to the user or is a system category
        if (category.user_id !== userId && !category.is_system) {
          return {
            error: {
              code: 'FORBIDDEN',
              message: 'Category not found or does not belong to user'
            }
          };
        }
      }

      const dbRow = convertTransactionToDbInsert(input, userId);

      const { data, error } = await supabase
        .from('transactions')
        .insert(dbRow)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'INSERT_ERROR',
            message: `Failed to create transaction: ${error.message}`
          }
        };
      }

      // Update account opening date if transaction date is earlier than current opening date
      const transactionDate = new Date(input.date);
      transactionDate.setHours(0, 0, 0, 0);
      
      const accountOpeningDate = account.opening_date 
        ? new Date(account.opening_date)
        : account.created_at 
        ? new Date(account.created_at)
        : new Date();
      accountOpeningDate.setHours(0, 0, 0, 0);

      // If transaction date is earlier than opening date, update opening date
      if (transactionDate < accountOpeningDate) {
        await supabase
          .from('accounts')
          .update({ opening_date: input.date })
          .eq('id', input.accountId)
          .eq('user_id', userId);
      }

      // For transfers, also update destination account opening date if needed
      if (input.type === 'transfer' && destinationAccount) {
        const destOpeningDate = destinationAccount.opening_date
          ? new Date(destinationAccount.opening_date)
          : destinationAccount.created_at
          ? new Date(destinationAccount.created_at)
          : new Date();
        destOpeningDate.setHours(0, 0, 0, 0);

        if (transactionDate < destOpeningDate && input.destinationAccountId) {
          await supabase
            .from('accounts')
            .update({ opening_date: input.date })
            .eq('id', input.destinationAccountId)
            .eq('user_id', userId);
        }
      }

      return {
        data: convertDbRowToTransaction(data)
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Update transaction
   */
  async updateTransaction(
    transactionId: string,
    userId: string,
    updates: UpdateTransactionInput
  ): Promise<ServiceResponse<Transaction>> {
    try {
      // Validate input
      const validation = validateUpdateTransaction(updates);
      if (!validation.success) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: validation.error?.issues.map((e: any) => e.message).join(', ') || 'Validation failed'
          }
        };
      }

      // Verify transaction belongs to user
      const { data: existing, error: fetchError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !existing) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'Transaction not found'
          }
        };
      }

      // Verify category belongs to user if provided
      if (updates.categoryId !== undefined && updates.categoryId !== null) {
        const { data: category, error: categoryError } = await supabase
          .from('categories')
          .select('id')
          .or(`id.eq.${updates.categoryId},is_system.eq.true`)
          .single();

        if (categoryError || !category) {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found'
            }
          };
        }
      }

      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (updates.categoryId !== undefined) {
        updateData.category_id = updates.categoryId;
        // Keep status aligned with categorisation unless an explicit status was provided
        if (updates.status === undefined) {
          updateData.status = updates.categoryId ? 'completed' : 'pending';
        }
      }
      if (updates.amount !== undefined) {
        updateData.amount = updates.amount;
      }
      if (updates.description !== undefined) {
        updateData.description = updates.description;
      }
      if (updates.merchantName !== undefined) {
        updateData.merchant_name = updates.merchantName;
      }
      if (updates.notes !== undefined) {
        updateData.notes = updates.notes;
      }
      if (updates.referenceNumber !== undefined) {
        updateData.reference_number = updates.referenceNumber;
      }
      if (updates.payee !== undefined) {
        updateData.payee = updates.payee;
      }
      if (updates.balanceAfter !== undefined) {
        updateData.balance_after = updates.balanceAfter;
      }
      if (updates.chequeNumber !== undefined) {
        updateData.cheque_number = updates.chequeNumber;
      }
      if (updates.type !== undefined) {
        updateData.type = updates.type;
      }
      if (updates.status !== undefined) {
        updateData.status = updates.status;
      }
      if (updates.flaggedForReview !== undefined) {
        updateData.flagged_for_review = updates.flaggedForReview;
      }
      if (updates.statusReason !== undefined) {
        updateData.status_reason = updates.statusReason;
      }
      if (updates.businessUsePercent !== undefined) {
        updateData.business_use_percent = updates.businessUsePercent;
      }
      if (updates.gstStatus !== undefined) {
        updateData.gst_status = updates.gstStatus;
      }
      if (updates.receiptAttached !== undefined) {
        updateData.receipt_attached = updates.receiptAttached;
      }

      const { data, error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', transactionId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to update transaction: ${error.message}`
          }
        };
      }

      return {
        data: convertDbRowToTransaction(data)
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Delete transaction
   */
  async deleteTransaction(transactionId: string): Promise<DeleteResponse> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        return {
          success: false,
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated'
          }
        };
      }

      // Verify transaction belongs to user
      const { data: existing, error: fetchError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !existing) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Transaction not found'
          }
        };
      }

      // Check if transaction has splits (only if the table exists)
      // If the table doesn't exist, we'll skip this check and proceed with deletion
      const { data: splits, error: splitsError } = await supabase
        .from('transaction_splits')
        .select('id')
        .eq('transaction_id', transactionId)
        .limit(1);

      // Only fail if the error is not a "table doesn't exist" error
      // Supabase returns errors for missing tables that we can safely ignore
      if (splitsError) {
        const errorMessage = splitsError.message || '';
        const isTableNotFound = 
          splitsError.code === 'PGRST116' ||
          errorMessage.includes('does not exist') ||
          errorMessage.includes('Could not find the table') ||
          errorMessage.includes('relation') && errorMessage.includes('does not exist');
        
        if (!isTableNotFound) {
          return {
            success: false,
            error: {
              code: 'QUERY_ERROR',
              message: `Failed to check splits: ${splitsError.message}`
            }
          };
        }
        // If table doesn't exist, continue with deletion (splits check is skipped)
      }

      // If splits exist, prevent deletion
      if (splits && splits.length > 0) {
        return {
          success: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Cannot delete transaction with active splits'
          }
        };
      }

      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionId)
        .eq('user_id', userId);

      if (error) {
        return {
          success: false,
          error: {
            code: 'DELETE_ERROR',
            message: `Failed to delete transaction: ${error.message}`
          }
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Categorise transaction
   */
  async categoriseTransaction(
    transactionId: string,
    userId: string,
    categoryId: string | null
  ): Promise<ServiceResponse<Transaction>> {
    try {
      // Verify transaction belongs to user
      const { data: existing, error: fetchError } = await supabase
        .from('transactions')
        .select('id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !existing) {
        const errorMessage = fetchError 
          ? `Transaction not found: ${fetchError.message || fetchError.details || 'Transaction does not exist or does not belong to user'}`
          : 'Transaction not found: Transaction does not exist or does not belong to user';
        return {
          error: {
            code: 'NOT_FOUND',
            message: errorMessage
          }
        };
      }

      // Verify category belongs to user if provided
      if (categoryId) {
        // First check if category exists with the specific ID
        const { data: category, error: categoryError } = await supabase
          .from('categories')
          .select('id, user_id, is_system')
          .eq('id', categoryId)
          .single();

        if (categoryError || !category) {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found'
            }
          };
        }

        // Then verify it belongs to the user or is a system category
        if (category.user_id !== userId && !category.is_system) {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found'
            }
          };
        }
      }

      // Build update object - always include category_id (can be null for uncategorising)
      // When categorising (categoryId is not null), set status to 'completed'
      // When uncategorising (categoryId is null), set status to 'pending'
      // Note: updated_at is automatically set by database trigger
      const updateData: any = {
        category_id: categoryId, // Can be null for uncategorising
        status: categoryId ? 'completed' : 'pending' // Set status based on whether category is assigned
      };

      // Log the update data for debugging
      console.log('Updating transaction:', {
        transactionId,
        userId,
        updateData,
        categoryId
      });

      // First, perform the update
      const { error: updateError } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', transactionId)
        .eq('user_id', userId);

      if (updateError) {
        // Log the full error for debugging
        console.error('Supabase update error:', {
          updateError,
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code,
          fullError: JSON.stringify(updateError, null, 2)
        });
        // Include more error details for debugging
        const errorMessage = updateError.message || updateError.details || updateError.hint || JSON.stringify(updateError) || 'Unknown database error';
        const errorCode = updateError.code || 'UPDATE_ERROR';
        return {
          error: {
            code: 'UPDATE_ERROR',
            message: `Failed to categorise transaction: ${errorMessage} (Code: ${errorCode})`
          }
        };
      }

      // Then, fetch the updated transaction
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (error) {
        // Log the full error for debugging
        console.error('Supabase select error:', {
          error,
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          fullError: JSON.stringify(error, null, 2)
        });
        // Include more error details for debugging
        const errorMessage = error.message || error.details || error.hint || JSON.stringify(error) || 'Unknown database error';
        const errorCode = error.code || 'SELECT_ERROR';
        return {
          error: {
            code: 'SELECT_ERROR',
            message: `Failed to fetch updated transaction: ${errorMessage} (Code: ${errorCode})`
          }
        };
      }

      return {
        data: convertDbRowToTransaction(data)
      };
    } catch (error: any) {
      // Log the full error for debugging
      console.error('Error in categoriseTransaction:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error?.message || error?.toString() || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Bulk categorise transactions
   */
  async bulkCategoriseTransactions(
    transactionIds: string[],
    userId: string,
    categoryId: string | null
  ): Promise<ServiceResponse<{ updated: number; failed: number; errors: string[] }>> {
    try {
      // Verify category exists if provided (null is allowed for uncategorising)
      if (categoryId) {
        // First check if category exists with the specific ID
        const { data: category, error: categoryError } = await supabase
          .from('categories')
          .select('id, user_id, is_system')
          .eq('id', categoryId)
          .single();

        if (categoryError || !category) {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found'
            }
          };
        }

        // Then verify it belongs to the user or is a system category
        if (category.user_id !== userId && !category.is_system) {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Category not found'
            }
          };
        }
      }

      let updated = 0;
      let failed = 0;
      const errors: string[] = [];

      for (const transactionId of transactionIds) {
        const result = await this.categoriseTransaction(transactionId, userId, categoryId);
        if (result.error) {
          failed++;
          // Include error code and full message for better debugging
          const errorDetails = result.error.code 
            ? `${result.error.message} (Error Code: ${result.error.code})`
            : result.error.message;
          errors.push(`Transaction ${transactionId}: ${errorDetails}`);
          // Log the error for debugging
          console.error(`Failed to categorise transaction ${transactionId}:`, result.error);
        } else {
          updated++;
        }
      }

      return {
        data: { updated, failed, errors }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Bulk categorise transactions by matching descriptions with previous categorised transactions
   * For each transaction, finds the most recent transaction with the same description that has a category
   * If found, categorises the transaction with that category and sets status to 'completed'
   * If not found, leaves the transaction uncategorised
   */
  async bulkCategoriseByDescription(
    transactionIds: string[],
    userId: string
  ): Promise<ServiceResponse<{ matched: number; unmatched: number; failed: number; errors: string[] }>> {
    try {
      // First, fetch the transactions to get their descriptions
      const { data: transactions, error: fetchError } = await supabase
        .from('transactions')
        .select('id, description, merchant_name')
        .in('id', transactionIds)
        .eq('user_id', userId);

      if (fetchError) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch transactions: ${fetchError.message}`
          }
        };
      }

      if (!transactions || transactions.length === 0) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'No transactions found'
          }
        };
      }

      let matched = 0;
      let unmatched = 0;
      let failed = 0;
      const errors: string[] = [];

      const findPriorCategoryId = async (
        transaction: { id: string; description: string | null; merchant_name: string | null },
        requireMerchant: boolean
      ): Promise<{ categoryId: string | null; error?: string }> => {
        let query = supabase
          .from('transactions')
          .select('category_id')
          .eq('user_id', userId)
          .neq('id', transaction.id)
          .not('category_id', 'is', null)
          .order('date', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(1);

        if (transaction.description) {
          query = query.eq('description', transaction.description);
        }

        if (requireMerchant && transaction.merchant_name) {
          query = query.eq('merchant_name', transaction.merchant_name);
        }

        const { data, error } = await query;
        if (error) {
          return { categoryId: null, error: error.message };
        }
        return { categoryId: data?.[0]?.category_id ?? null };
      };

      // Process each transaction
      for (const transaction of transactions) {
        try {
          // Prefer description + merchant match, then fall back to description-only.
          // Newer imports often set merchant_name while older categorised rows left it null.
          let categoryId: string | null = null;

          if (transaction.merchant_name) {
            const strict = await findPriorCategoryId(transaction, true);
            if (strict.error) {
              failed++;
              errors.push(`Transaction ${transaction.id}: Failed to find matching transaction - ${strict.error}`);
              continue;
            }
            categoryId = strict.categoryId;
          }

          if (!categoryId) {
            const loose = await findPriorCategoryId(transaction, false);
            if (loose.error) {
              failed++;
              errors.push(`Transaction ${transaction.id}: Failed to find matching transaction - ${loose.error}`);
              continue;
            }
            categoryId = loose.categoryId;
          }

          if (!categoryId) {
            // No matching transaction found - leave uncategorised
            unmatched++;
            continue;
          }

          // Found a matching transaction with a category - categorise this transaction
          const result = await this.categoriseTransaction(transaction.id, userId, categoryId);

          if (result.error) {
            failed++;
            const errorDetails = result.error.code 
              ? `${result.error.message} (Error Code: ${result.error.code})`
              : result.error.message;
            errors.push(`Transaction ${transaction.id}: ${errorDetails}`);
          } else {
            matched++;
          }
        } catch (err: any) {
          failed++;
          errors.push(`Transaction ${transaction.id}: ${err.message || 'An unexpected error occurred'}`);
        }
      }

      return {
        data: { matched, unmatched, failed, errors }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Get category suggestions based on similar transactions
   * Returns top 3 most frequently used categories for similar transactions
   */
  async getCategorySuggestions(
    transaction: { merchantName?: string; description: string },
    userId: string,
    limit: number = 3
  ): Promise<ServiceResponse<Array<{ categoryId: string; categoryName: string; count: number }>>> {
    try {
      // Build query to find similar transactions
      let query = supabase
        .from('transactions')
        .select('category_id, categories!inner(id, name)')
        .eq('user_id', userId)
        .not('category_id', 'is', null);

      // Match by merchant name if available, otherwise by description
      if (transaction.merchantName) {
        query = query.ilike('merchant_name', `%${transaction.merchantName}%`);
      } else {
        // Extract key words from description for matching
        const descriptionWords = transaction.description
          .split(/\s+/)
          .filter(word => word.length > 3)
          .slice(0, 3); // Use first 3 meaningful words
        
        if (descriptionWords.length > 0) {
          const descriptionPattern = descriptionWords.join('|');
          query = query.or(`description.ilike.%${descriptionPattern}%,merchant_name.ilike.%${descriptionPattern}%`);
        } else {
          query = query.ilike('description', `%${transaction.description}%`);
        }
      }

      // Limit to recent transactions (last 6 months) for better relevance
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      query = query.gte('date', sixMonthsAgo.toISOString().split('T')[0]);

      // Limit results for performance
      query = query.limit(100);

      const { data, error } = await query;

      if (error) {
        return {
          error: {
            code: 'QUERY_ERROR',
            message: `Failed to fetch category suggestions: ${error.message}`
          }
        };
      }

      if (!data || data.length === 0) {
        return {
          data: []
        };
      }

      // Count category occurrences
      const categoryCounts = new Map<string, { categoryId: string; categoryName: string; count: number }>();
      
      data.forEach((row: any) => {
        if (row.category_id && row.categories) {
          const categoryId = row.category_id;
          const categoryName = row.categories.name;
          
          if (categoryCounts.has(categoryId)) {
            categoryCounts.get(categoryId)!.count++;
          } else {
            categoryCounts.set(categoryId, {
              categoryId,
              categoryName,
              count: 1
            });
          }
        }
      });

      // Sort by count (descending) and return top N
      const suggestions = Array.from(categoryCounts.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);

      return {
        data: suggestions
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error?.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Bulk tag transactions
   */
  async bulkTagTransactions(
    transactionIds: string[],
    userId: string,
    tagIds: string[]
  ): Promise<ServiceResponse<{ updated: number }>> {
    try {
      // Verify tags exist and belong to user
      const { data: tags, error: tagsError } = await supabase
        .from('transaction_tags')
        .select('id')
        .in('id', tagIds)
        .eq('user_id', userId);

      if (tagsError || !tags || tags.length !== tagIds.length) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'One or more tags not found'
          }
        };
      }

      let updated = 0;

      for (const transactionId of transactionIds) {
        // Verify transaction belongs to user
        const { data: transaction, error: transactionError } = await supabase
          .from('transactions')
          .select('id')
          .eq('id', transactionId)
          .eq('user_id', userId)
          .single();

        if (transactionError || !transaction) {
          continue;
        }

        // Assign tags
        const assignments = tagIds.map(tagId => ({
          transaction_id: transactionId,
          tag_id: tagId
        }));

        const { error: assignError } = await supabase
          .from('transaction_tag_assignments')
          .upsert(assignments, { onConflict: 'transaction_id,tag_id' });

        if (!assignError) {
          updated++;
        }
      }

      return {
        data: { updated }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Bulk delete transactions
   */
  async bulkDeleteTransactions(
    transactionIds: string[]
  ): Promise<ServiceResponse<{ deleted: number; failed: number }>> {
    try {
      let deleted = 0;
      let failed = 0;

      for (const transactionId of transactionIds) {
        const result = await this.deleteTransaction(transactionId);
        if (result.success) {
          deleted++;
        } else {
          failed++;
        }
      }

      return {
        data: { deleted, failed }
      };
    } catch (error: any) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred'
        }
      };
    }
  }

  /**
   * Get transactions for multiple accounts
   * Returns a Map of accountId -> Transaction[]
   * Used for calculating account balances
   */
  async getTransactionsForAccounts(
    accountIds: string[],
    userId: string
  ): Promise<Map<string, Transaction[]>> {
    try {
      if (!accountIds || accountIds.length === 0) {
        return new Map();
      }

      // Fetch all transactions for these accounts (filter by account_id only)
      // Note: Supabase has a default limit of 1000 rows, so we need to explicitly set a high limit
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .in('account_id', accountIds)
        .order('date', { ascending: true })
        .limit(100000); // Set a high limit to get all transactions (Supabase default is 1000)

      if (error) {
        console.error('Error fetching transactions for accounts:', error);
        return new Map();
      }

      // Group transactions by account ID
      const transactionsMap = new Map<string, Transaction[]>();
      
      // Initialize map with empty arrays for all accounts
      accountIds.forEach(accountId => {
        transactionsMap.set(accountId, []);
      });

      // Group transactions by account_id
      (data || []).forEach((row) => {
        const transaction = convertDbRowToTransaction(row);
        if (transaction.accountId && accountIds.includes(transaction.accountId)) {
          const existing = transactionsMap.get(transaction.accountId) || [];
          existing.push(transaction);
          transactionsMap.set(transaction.accountId, existing);
        }
      });

      return transactionsMap;
    } catch (error: any) {
      console.error('Unexpected error fetching transactions for accounts:', error);
      return new Map();
    }
  }

  /**
   * Bulk-update tax / claim fields on transactions (business expense prep).
   */
  async bulkUpdateTransactionTaxFields(
    transactionIds: string[],
    userId: string,
    updates: Pick<
      UpdateTransactionInput,
      'businessUsePercent' | 'gstStatus' | 'receiptAttached'
    >,
    options?: { onlyUnsetBusinessUsePercent?: boolean }
  ): Promise<ServiceResponse<{ updated: number; failed: number; errors: string[]; skipped?: number }>> {
    if (!transactionIds.length) {
      return { data: { updated: 0, failed: 0, errors: [] } };
    }

    const hasField =
      updates.businessUsePercent !== undefined ||
      updates.gstStatus !== undefined ||
      updates.receiptAttached !== undefined;

    if (!hasField) {
      return {
        error: {
          code: 'VALIDATION_ERROR',
          message: 'At least one tax field must be provided',
        },
      };
    }

    try {
      const validation = validateUpdateTransaction(updates);
      if (!validation.success) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message:
              validation.error?.issues.map((e: { message: string }) => e.message).join(', ') ||
              'Validation failed',
          },
        };
      }

      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (updates.businessUsePercent !== undefined) {
        updateData.business_use_percent = updates.businessUsePercent;
      }
      if (updates.gstStatus !== undefined) {
        updateData.gst_status = updates.gstStatus;
      }
      if (updates.receiptAttached !== undefined) {
        updateData.receipt_attached = updates.receiptAttached;
      }

      const chunkSize = 200;
      let updated = 0;
      let failed = 0;
      const errors: string[] = [];

      let skipped = 0;

      for (let i = 0; i < transactionIds.length; i += chunkSize) {
        const chunk = transactionIds.slice(i, i + chunkSize);
        let query = supabase
          .from('transactions')
          .update(updateData)
          .in('id', chunk)
          .eq('user_id', userId);

        if (options?.onlyUnsetBusinessUsePercent && updates.businessUsePercent !== undefined) {
          query = query.is('business_use_percent', null);
        }

        const { data, error } = await query.select('id');

        if (error) {
          failed += chunk.length;
          errors.push(error.message);
          continue;
        }
        updated += data?.length ?? 0;
        const updatedIds = new Set((data ?? []).map((row) => row.id));
        for (const id of chunk) {
          if (!updatedIds.has(id)) {
            if (options?.onlyUnsetBusinessUsePercent && updates.businessUsePercent !== undefined) {
              skipped++;
            } else {
              failed++;
              errors.push(`Transaction ${id}: not found or not updated`);
            }
          }
        }
      }

      return { data: { updated, failed, errors, skipped } };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message,
        },
      };
    }
  }
}

// Export singleton instance
export const transactionService = new TransactionService();
